import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { faEnvelope } from "@fortawesome/pro-regular-svg-icons";

import { Modal } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";

import { EmailAccountRow } from "./EmailAccountRow";
import { SecondaryEmailsSection } from "./SecondaryEmailsSection";
import { SettingsCard } from "./SettingsCard";
import { ConnectedBadge, SettingsSubBlock } from "./SettingsSection";
import { useUnipileConnect } from "../hooks/useUnipileConnect";
import { isEmailLoggedOut } from "../schema/email";

export const EmailConnection: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`account.connectedAccounts.${key}`);

	const { user } = useAppSelector(authSelector);

	const {
		isLoading,
		error,
		setError,
		showConfirmModal,
		setShowConfirmModal,
		startValidation,
		setStartValidation,
		connect,
	} = useUnipileConnect({ type: "email" });

	// The primary inbox is shown by default; any additional inboxes collapse behind a toggle.
	const primaryEmail = useMemo(
		() => user?.emails?.find((email) => email.type === "primary"),
		[user?.emails]
	);

	const secondaryEmails = useMemo(
		() => (user?.emails ?? []).filter((email) => email.type !== "primary"),
		[user?.emails]
	);

	const hasEmail = !!primaryEmail || secondaryEmails.length > 0;

	// Reflect the real per-account status (email.status) in the card badge: green only when every
	// connected inbox is healthy, otherwise flag that at least one is logged out.
	const anyLoggedOut = useMemo(
		() => (user?.emails ?? []).some((email) => isEmailLoggedOut(email.status)),
		[user?.emails]
	);

	const badgeLabel = !hasEmail
		? ts("notConnected")
		: anyLoggedOut
			? ts("loggedOut")
			: ts("connected");

	return (
		<SettingsCard
			headerRight={<ConnectedBadge connected={hasEmail && !anyLoggedOut} label={badgeLabel} />}
			icon={faEnvelope}
			subtitle={ts("emailSubtitle")}
			title={ts("emailTitle")}
		>
			<div className="px-5 py-4">
				{hasEmail ? (
					<div className="flex flex-col gap-3">
						{primaryEmail && <EmailAccountRow email={primaryEmail} />}
						<SecondaryEmailsSection emails={secondaryEmails} />
					</div>
				) : (
					<SettingsSubBlock>
						<div className="flex flex-wrap items-center justify-between gap-3">
							<p className="text-[12.5px] text-text-meta">{ts("emailConnectHint")}</p>
							<Button
								color={ButtonColor.ACTION}
								size={ButtonSize.S}
								title={ts("connectEmail")}
								type="button"
								onClick={() => setShowConfirmModal(true)}
							/>
						</div>
					</SettingsSubBlock>
				)}
			</div>

			<Modal
				disableSave={!hasEmail}
				handleClose={async () => {
					setStartValidation(false);
				}}
				handleSave={() => {
					setStartValidation(false);
				}}
				isLoading={isLoading}
				isOpened={startValidation}
				size="sm"
				submitButtonText={"Done"}
				title={!hasEmail ? "Currently connecting your E-Mail Address" : "Successfully connected"}
			>
				{!hasEmail ? (
					<div className="text-text-meta text-sm">
						Please wait while we validate your E-Mail address. This may take a few seconds.
					</div>
				) : (
					<div className="text-text-meta text-sm">
						You are currently connected to your E-Mail address.
					</div>
				)}
			</Modal>
			<Modal
				handleClose={
					!error
						? async () => {
								setShowConfirmModal(false);
							}
						: undefined
				}
				handleSave={() => {
					if (error) {
						setError(null);
						setShowConfirmModal(false);
					} else {
						connect();
					}
				}}
				isLoading={isLoading}
				isOpened={showConfirmModal}
				size="sm"
				submitButtonText={error ? "Close" : "Start E-Mail connection"}
				title={"Connect your E-Mail Address"}
			>
				{error && <div className="text-text-meta text-sm">{error}</div>}
				{!error && (
					<>
						<div className="text-text-meta text-sm">
							Confirm this modal to connect your E-Mail address.
						</div>
						<br />
						<div className="text-text-meta text-sm">
							By clicking on the button, we will open a new tab to connect your E-Mail address.{" "}
							<br />
							<span className="font-bold">Popups need to be allowed in your browser.</span>
						</div>
					</>
				)}
			</Modal>
		</SettingsCard>
	);
};
